import { QueryClientProvider } from '@tanstack/react-query'
import {
  Outlet,
  type RouterHistory,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'

import { NotFoundPage } from '@/app/not-found'
import { RequireAuth } from '@/app/require-auth'
import { AppShell } from '@/features/nav/shell'
import { RestaurantAccountsPage } from '@/features/back-office/accounts-page'
import { RestaurantAuditLogPage } from '@/features/back-office/audit-log-page'
import { RestaurantFeedbackPage } from '@/features/back-office/feedback-page'
import { BranchInfoPage } from '@/features/back-office/branch-info-page'
import { BranchStaffPage } from '@/features/back-office/branch-staff-page'
import { BranchMenuPage } from '@/features/back-office/branch-menu-page'
import { BranchSettingsPage } from '@/features/back-office/branch-settings-page'
import { BranchTablesPage } from '@/features/back-office/tables-page'
import { BranchQueuePage } from '@/features/back-office/queue-page'
import { CreateOrderPage } from '@/features/back-office/create-order-page'
import { OrderDetailsPage } from '@/features/back-office/order-page'
import { SessionDetailsPage } from '@/features/back-office/session-page'
import { BranchSearchPage } from '@/features/back-office/search-page'
import { StatisticsPage } from '@/features/back-office/statistics-page'
import { BestsellersPage, OrdersOfDatePage } from '@/features/back-office/statistics-lists'
import { parseDateListSearch, parseMonthSearch, parseSpanSearch, parseStatisticsSearch } from '@/features/back-office/statistics-params'
import { parseSearchPageSearch } from '@/features/back-office/search-params'
import { BranchSessionsPage } from '@/features/back-office/sessions-page'
import { QrPrintPage } from '@/features/back-office/qr-print-page'
import { parseQrPrintSearch } from '@/features/back-office/qr-print-search'
import { RestaurantBranchesPage } from '@/features/back-office/branches-page'
import { parseAccountsSearch } from '@/features/back-office/accounts-search'
import { MyRestaurantsPage } from '@/features/back-office/my-restaurants-page'
import { CreateOwnRestaurantPage } from '@/features/back-office/create-restaurant-page'
import { RestaurantMenuPage } from '@/features/back-office/menu-page'
import { BranchManageIndex, RestaurantLayout } from '@/features/back-office/restaurant-layout'
import { RestaurantSettingsPage } from '@/features/back-office/settings-page'
import { ForgotPasswordPage } from '@/features/auth/forgot-password-page'
import { LoginPage } from '@/features/auth/login-page'
import { RegisterPage } from '@/features/auth/register-page'
import { GuidePage } from '@/features/guide/guide-page'
import { BranchLandingPage } from '@/features/landing/branch-page'
import { RestaurantLandingPage } from '@/features/landing/restaurant-page'
import { RomiLandingPage } from '@/features/landing/romi-page'
import { NotificationsPage } from '@/features/notifications/page'
import { MyOrdersPage } from '@/features/ordering/my-orders-page'
import { TableOrderingPage, TakeAwayOrderingPage } from '@/features/ordering/ordering-page'
import { SessionTrackingPage } from '@/features/ordering/session-page'
import { OrderTrackingPage } from '@/features/ordering/tracking-page'
import { parseOtpSearch } from '@/features/otp-requests/api'
import { OtpRequestListPage } from '@/features/otp-requests/list-page'
import { CreatePlatformUserPage } from '@/features/platform-users/create-page'
import { PlatformUserDetailPage } from '@/features/platform-users/detail-page'
import { PlatformUserListPage } from '@/features/platform-users/list-page'
import { RequireAdmin } from '@/features/platform-users/require-admin'
import { parseSystemLogSearch } from '@/features/system-log/api'
import { SystemLogPage } from '@/features/system-log/list-page'
import { CleanupPage } from '@/features/cleanup/page'
import { parseAuditSearch, parsePlatformAuditSearch } from '@/features/audit-log/api'
import { PlatformAuditLogPage } from '@/features/audit-log/platform-page'
import { parseFeedbackSearch } from '@/features/feedback/api'
import { PlatformFeedbackPage } from '@/features/feedback/platform-page'
import { ProfilePage } from '@/features/profile/profile-page'
import { CreateRestaurantPage } from '@/features/restaurants/create-page'
import { RestaurantDetailPage } from '@/features/restaurants/detail-page'
import { RestaurantListPage } from '@/features/restaurants/list-page'
import { parseRestaurantListSearch } from '@/features/restaurants/list-search'
import type { System } from '@/lib/auth-store'
import { isPlatformHost } from '@/lib/host'
import { parsePageSearch } from '@/lib/page-search'
import { queryClient } from '@/lib/query'
import {
  homePathFor,
  loginPathFor,
  loginSearchFor,
  parseLoginSearchFor,
  parseRegisterSearchFor,
} from '@/lib/return-path'
import { ensureAuth } from '@/lib/session'

function rootOptions() {
  return {
    component: () => (
      <QueryClientProvider client={queryClient}>
        <Outlet />
      </QueryClientProvider>
    ),
  }
}

/** First-visit guard (T15): unknown → ask `…/me`; anonymous → login with return path. */
function guard(system: System) {
  return async ({ location }: { location: { pathname: string; searchStr: string } }) => {
    const auth = await ensureAuth(system)
    if (auth.status !== 'authenticated') {
      throw redirect({ to: loginPathFor(system), search: loginSearchFor(system, location) })
    }
  }
}

function authLayout(system: System) {
  return () => (
    <AppShell system={system}>
      <RequireAuth system={system} />
    </AppShell>
  )
}

/** The shell and guard around one restaurant's (or branch's) manage pages, then the scope gate. */
function manageLayout(level: 'restaurant' | 'branch') {
  return () => (
    <AppShell system="restaurant">
      <RequireAuth system="restaurant">
        <RestaurantLayout level={level} />
      </RequireAuth>
    </AppShell>
  )
}

// ---- Main host (romi.io.vn): restaurant app under /app, tenants at root ----

const rootRoute = createRootRoute(rootOptions())

// H1: the public pages at the path root (URL scheme, 2026-09-01) — the Romi
// intro, then tenants by slug. Printed QRs carry the two slug paths (G3).
const rootIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: RomiLandingPage,
})

const restaurantLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$restaurantSlug',
  component: RestaurantLandingPage,
})

const branchLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$restaurantSlug/$branchSlug',
  component: BranchLandingPage,
})

/** Every main-host SPA page lives under `/app` (URL scheme, 2026-09-01). */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
})

// The public user guide (2026-10-02): no login, under `/app` like every SPA page.
const guideIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/guide',
  beforeLoad: () => {
    throw redirect({ to: '/app/guide/$guide', params: { guide: 'create-restaurant' }, replace: true })
  },
})

const guideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/guide/$guide',
  component: GuidePage,
})

const loginRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/login',
  validateSearch: parseLoginSearchFor('restaurant'),
  component: () => <LoginPage system="restaurant" />,
})

// Restaurant only: platform accounts are admin-created (PRD).
const registerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/register',
  validateSearch: parseRegisterSearchFor('restaurant'),
  component: RegisterPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/forgot-password',
  validateSearch: parseRegisterSearchFor('restaurant'),
  component: () => <ForgotPasswordPage system="restaurant" />,
})

const restaurantAuthRoute = createRoute({
  getParentRoute: () => appRoute,
  id: 'restaurant-auth',
  beforeLoad: guard('restaurant'),
  component: authLayout('restaurant'),
})

// `/app` itself is nothing: the guard has run, so send the user to the account area.
const restaurantHomeRoute = createRoute({
  getParentRoute: () => restaurantAuthRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: homePathFor('restaurant') })
  },
})

const profileRoute = createRoute({
  getParentRoute: () => restaurantAuthRoute,
  path: '/profile',
  component: () => <ProfilePage system="restaurant" />,
})

// H3: the bell's page; `page` from the pager.
const notificationsRoute = createRoute({
  getParentRoute: () => restaurantAuthRoute,
  path: '/notifications',
  validateSearch: parsePageSearch,
  component: () => <NotificationsPage system="restaurant" />,
})

// H6: the account's orders across every venue; `page` pages the settled ones.
const myOrdersRoute = createRoute({
  getParentRoute: () => restaurantAuthRoute,
  path: '/my-orders',
  validateSearch: parsePageSearch,
  component: MyOrdersPage,
})

// E6: the back office entry — the user's restaurants, an account page like the rest of /app.
const myRestaurantsRoute = createRoute({
  getParentRoute: () => restaurantAuthRoute,
  path: '/restaurants',
  component: MyRestaurantsPage,
})

// K1: self-serve creation; the login guard brings a "Start free" visitor back here.
const createOwnRestaurantRoute = createRoute({
  getParentRoute: () => restaurantAuthRoute,
  path: '/restaurants/new',
  component: CreateOwnRestaurantPage,
})

// URL scheme (2026-09-11): one restaurant's manage pages live at `/<r>/manage`, one
// branch's at `/<r>/<b>/manage` — flat under the root, since the slug routes above are
// pages, not layouts. Each carries its own guard and shell; everything beneath shares
// one loaded scope (accent, nav, role), resolved from the slug.
const restaurantScopeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$restaurantSlug/manage',
  beforeLoad: guard('restaurant'),
  component: manageLayout('restaurant'),
})

// The manage index is nothing: the landing page holds every role's way in — the branch
// links, and for manager and above the Branches page (2026-09-15).
const restaurantScopeIndexRoute = createRoute({
  getParentRoute: () => restaurantScopeRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/$restaurantSlug', params, replace: true })
  },
})

const restaurantSettingsRoute = createRoute({
  getParentRoute: () => restaurantScopeRoute,
  path: '/settings',
  component: RestaurantSettingsPage,
})

// E7: role roster + grant/revoke, manager and above.
const restaurantAccountsRoute = createRoute({
  getParentRoute: () => restaurantScopeRoute,
  path: '/accounts',
  validateSearch: parseAccountsSearch,
  component: RestaurantAccountsPage,
})

// I2: who changed what, manager and above; the filters ride in the URL.
const restaurantAuditLogRoute = createRoute({
  getParentRoute: () => restaurantScopeRoute,
  path: '/audit-log',
  validateSearch: parseAuditSearch,
  component: RestaurantAuditLogPage,
})

// I3: what customers wrote, manager and above; the branch filter rides in the URL.
const restaurantFeedbackRoute = createRoute({
  getParentRoute: () => restaurantScopeRoute,
  path: '/feedback',
  validateSearch: parseFeedbackSearch,
  component: RestaurantFeedbackPage,
})

// F3: categories + items, manager and above.
const restaurantMenuRoute = createRoute({
  getParentRoute: () => restaurantScopeRoute,
  path: '/menu',
  component: RestaurantMenuPage,
})

// F4: the caller's branches, and the branch scope every branch page nests under.
const restaurantBranchesRoute = createRoute({
  getParentRoute: () => restaurantScopeRoute,
  path: '/branches',
  component: RestaurantBranchesPage,
})

const branchScopeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$restaurantSlug/$branchSlug/manage',
  beforeLoad: guard('restaurant'),
  component: manageLayout('branch'),
})

// A page, not a `beforeLoad` redirect: the destination depends on the role, which the layout loads.
const branchScopeIndexRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/',
  component: BranchManageIndex,
})

// G9: the order queue, every role within the branch.
const branchQueueRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/queue',
  component: BranchQueuePage,
})

// G10: one order — details, returns, real weight; every role within the branch.
const branchOrderRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/orders/$orderId',
  component: OrderDetailsPage,
})

// G11: open sessions and one session (close, change table); every role within the branch.
const branchSessionsRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/sessions',
  component: BranchSessionsPage,
})

const branchSessionRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/sessions/$sessionId',
  component: SessionDetailsPage,
})

// G12: staff create an order for a walk-in or a named customer; every role within the branch.
const branchCreateOrderRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/create-order',
  component: CreateOrderPage,
})

// G13: exact lookups of an order or a session; every role within the branch.
const branchSearchRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/search',
  validateSearch: parseSearchPageSearch,
  component: BranchSearchPage,
})

const branchMenuRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/menu',
  component: BranchMenuPage,
})

// G3: tables + QR codes, admin and above; the print page is one sign per visit.
const branchTablesRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/tables',
  validateSearch: parsePageSearch,
  component: BranchTablesPage,
})

const branchQrPrintRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/tables/print',
  validateSearch: parseQrPrintSearch,
  component: QrPrintPage,
})

// G4: open/close shop + ordering policy, admin and above.
const branchSettingsRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/settings',
  component: BranchSettingsPage,
})

// H5: sales over a day / month / year / range, the full bestseller list, the orders of a day — admin and above.
const branchStatisticsRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/statistics',
  validateSearch: parseStatisticsSearch,
  component: StatisticsPage,
})

const branchBestsellersRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/statistics/bestsellers',
  validateSearch: parseSpanSearch,
  component: BestsellersPage,
})

const branchOrdersOfDateRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/statistics/orders',
  validateSearch: parseDateListSearch,
  component: OrdersOfDatePage,
})

// H2: name, address, phone, hours, announcement — manager and above.
const branchInfoRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/info',
  component: BranchInfoPage,
})

// H8: the staff of one branch — admin in scope and above.
const branchStaffRoute = createRoute({
  getParentRoute: () => branchScopeRoute,
  path: '/staff',
  validateSearch: parsePageSearch,
  component: BranchStaffPage,
})

// G6: customer ordering at the tenant level (URL scheme 2026-09-01). Printed
// QRs carry exactly these paths (G3: `api/tables.rs::{TAKE_AWAY_SEGMENT,
// TABLE_SEGMENT}`) — changing them invalidates signs on tables.
const takeAwayOrderingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$restaurantSlug/$branchSlug/order',
  component: TakeAwayOrderingPage,
})

const tableOrderingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$restaurantSlug/$branchSlug/t/$tableCode',
  component: TableOrderingPage,
})

// G7: where placing an order lands (`o` = order code), and the party's view
// of a dine-in session (`s` = session code) — the third one-letter leaf.
const orderTrackingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$restaurantSlug/$branchSlug/o/$code',
  component: OrderTrackingPage,
})

const sessionTrackingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$restaurantSlug/$branchSlug/s/$code',
  component: SessionTrackingPage,
})

export const mainTree = rootRoute.addChildren([
  rootIndexRoute,
  restaurantLandingRoute,
  branchLandingRoute,
  takeAwayOrderingRoute,
  tableOrderingRoute,
  orderTrackingRoute,
  sessionTrackingRoute,
  restaurantScopeRoute.addChildren([
    restaurantScopeIndexRoute,
    restaurantSettingsRoute,
    restaurantAccountsRoute,
    restaurantAuditLogRoute,
    restaurantFeedbackRoute,
    restaurantMenuRoute,
    restaurantBranchesRoute,
  ]),
  branchScopeRoute.addChildren([
    branchScopeIndexRoute,
    branchQueueRoute,
    branchOrderRoute,
    branchSessionsRoute,
    branchSessionRoute,
    branchCreateOrderRoute,
    branchSearchRoute,
    branchMenuRoute,
    branchTablesRoute,
    branchQrPrintRoute,
    branchSettingsRoute,
    branchInfoRoute,
    branchStaffRoute,
    branchStatisticsRoute,
    branchBestsellersRoute,
    branchOrdersOfDateRoute,
  ]),
  appRoute.addChildren([
    guideIndexRoute,
    guideRoute,
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    restaurantAuthRoute.addChildren([
      restaurantHomeRoute,
      profileRoute,
      notificationsRoute,
      myOrdersRoute,
      myRestaurantsRoute,
      createOwnRestaurantRoute,
    ]),
  ]),
])

// ---- Platform host (platform.romi.io.vn): admin pages at the path root ----
// No tenant slugs exist here, so no `/app` group and no reserved-word rules.

const platformRootRoute = createRootRoute(rootOptions())

const platformLoginRoute = createRoute({
  getParentRoute: () => platformRootRoute,
  path: '/login',
  validateSearch: parseLoginSearchFor('platform'),
  component: () => <LoginPage system="platform" />,
})

const platformForgotPasswordRoute = createRoute({
  getParentRoute: () => platformRootRoute,
  path: '/forgot-password',
  validateSearch: parseRegisterSearchFor('platform'),
  component: () => <ForgotPasswordPage system="platform" />,
})

const platformAuthRoute = createRoute({
  getParentRoute: () => platformRootRoute,
  id: 'platform-auth',
  beforeLoad: guard('platform'),
  component: authLayout('platform'),
})

// The platform root is the restaurant list (2026-09-12); Profile sits in the avatar menu.
const platformHomeRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: homePathFor('platform'), search: { page: 1 } })
  },
})

const platformProfileRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/profile',
  component: () => <ProfilePage system="platform" />,
})

const platformNotificationsRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/notifications',
  validateSearch: parsePageSearch,
  component: () => <NotificationsPage system="platform" />,
})

// E3: restaurant management. Any platform user; admin-only pages start at E4.
const platformRestaurantsRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/restaurants',
  validateSearch: parseRestaurantListSearch,
  component: RestaurantListPage,
})

const platformRestaurantNewRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/restaurants/new',
  component: CreateRestaurantPage,
})

const platformRestaurantDetailRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/restaurants/$restaurantId',
  validateSearch: parseMonthSearch,
  component: RestaurantDetailPage,
})

// E4: platform-user management, admin-only (nav hides it; the gate covers typed URLs).
const platformUsersRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/users',
  validateSearch: parsePageSearch,
  component: () => (
    <RequireAdmin>
      <PlatformUserListPage />
    </RequireAdmin>
  ),
})

const platformUserNewRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/users/new',
  component: () => (
    <RequireAdmin>
      <CreatePlatformUserPage />
    </RequireAdmin>
  ),
})

const platformUserDetailRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/users/$userId',
  component: () => (
    <RequireAdmin>
      <PlatformUserDetailPage />
    </RequireAdmin>
  ),
})

// E5: the manual OTP queue, any platform user.
const platformOtpRequestsRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/otp-requests',
  validateSearch: parseOtpSearch,
  component: OtpRequestListPage,
})

// H4: the system log, any platform user (finer platform roles are future work).
const platformSystemLogRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/system-log',
  validateSearch: parseSystemLogSearch,
  component: SystemLogPage,
})

// H7: data cleanup, platform admins only.
const platformCleanupRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/cleanup',
  component: () => (
    <RequireAdmin notice="cleanup.adminOnly">
      <CleanupPage />
    </RequireAdmin>
  ),
})

// I2: what platform users did, platform admins only.
const platformAuditLogRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/audit-log',
  validateSearch: parsePlatformAuditSearch,
  component: () => (
    <RequireAdmin notice="audit.platform.adminOnly">
      <PlatformAuditLogPage />
    </RequireAdmin>
  ),
})

// I3: what restaurant users say about Romi; every platform user reads it.
const platformFeedbackRoute = createRoute({
  getParentRoute: () => platformAuthRoute,
  path: '/feedback',
  validateSearch: parsePageSearch,
  component: PlatformFeedbackPage,
})

export const platformTree = platformRootRoute.addChildren([
  platformLoginRoute,
  platformForgotPasswordRoute,
  platformAuthRoute.addChildren([
    platformHomeRoute,
    platformProfileRoute,
    platformNotificationsRoute,
    platformRestaurantsRoute,
    platformRestaurantNewRoute,
    platformRestaurantDetailRoute,
    platformUsersRoute,
    platformUserNewRoute,
    platformUserDetailRoute,
    platformOtpRequestsRoute,
    platformSystemLogRoute,
    platformCleanupRoute,
    platformAuditLogRoute,
    platformFeedbackRoute,
  ]),
])

const routerDefaults = {
  defaultPreload: 'intent',
  scrollRestoration: true,
} as const

export function createMainRouter(history?: RouterHistory) {
  return createRouter({
    routeTree: mainTree,
    ...routerDefaults,
    defaultNotFoundComponent: () => <NotFoundPage home="/" />,
    ...(history ? { history } : {}),
  })
}

export function createPlatformRouter(history?: RouterHistory) {
  return createRouter({
    routeTree: platformTree,
    ...routerDefaults,
    defaultNotFoundComponent: () => <NotFoundPage home="/" />,
    ...(history ? { history } : {}),
  })
}

/** One SPA bundle serves both hosts; the host picks the tree. */
export function createAppRouter(history?: RouterHistory) {
  return isPlatformHost() ? createPlatformRouter(history) : createMainRouter(history)
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createMainRouter> | ReturnType<typeof createPlatformRouter>
  }
}
